/* DON'T EDIT THIS FILE: edit original and run build again */ import { isPlainObject } from "../../framework/core/is-plain-object.ts";
import { toArray } from "../../framework/core/maybe-array.ts";
import {
  typeCheckerError,
  typeCheckerOkResult,
  typeCheckerSchemaFailResult,
} from "./type-checker-errors.ts";
import { typeCheckerResolveResult } from "./type-checker-resolve-result.ts";
import {
  TypeCheckerError,
  TypeCheckerErrorType,
  TypeCheckerObjectSchema,
  TypeCheckerRecurseChecker,
  TypeCheckerResult,
  TypeCheckerSchema,
  TypeCheckerStatus,
  WithAnyTypeCheckerSchemaOptions,
} from "./type-checker-types.ts";

type TypeCheckerObjectSubResult = {
  skip: boolean;
  key: string;
  result: TypeCheckerResult;
};

const objectHasUnexpectedKeys = (
  schema: TypeCheckerObjectSchema["schema"],
  mapsFromKeys: Record<string, true>,
  value: object,
  path: string
): TypeCheckerError | null => {
  const unexpected: any = {};
  let hasUnexpected = false;
  for (const key of Object.keys(value)) {
    if (!(key in schema) && !(key in mapsFromKeys)) {
      // @ts-expect-error implicit any
      const subValue = value[key];
      unexpected[key] = subValue;
      hasUnexpected = true;
    }
  }
  if (hasUnexpected) {
    return typeCheckerError(
      TypeCheckerErrorType.hadUnexpectedContent,
      { unexpected, value },
      path
    );
  }
  return null;
};

const checkObjectSubSchema = (
  checker: WithAnyTypeCheckerSchemaOptions<TypeCheckerSchema>,
  key: string,
  value: object,
  path: string,
  check: TypeCheckerRecurseChecker
): TypeCheckerObjectSubResult => {
  const subPath = path + "." + key;
  let effectiveKey: string;
  if (checker.mapsFrom) {
    effectiveKey = checker.mapsFrom;
  } else {
    effectiveKey = key;
  }
  if (!(effectiveKey in value)) {
    if (checker.optional) {
      return {
        skip: true,
        key: effectiveKey,
        result: typeCheckerOkResult(undefined),
      };
    }
    return {
      skip: false,
      key: effectiveKey,
      result: typeCheckerResolveResult(
        checker,
        undefined,
        path,
        typeCheckerSchemaFailResult(undefined, subPath)
      ),
    };
  }
  // @ts-expect-error implicit any
  const subValue = value[effectiveKey];
  return {
    skip: false,
    key: effectiveKey,
    result: check(checker, subValue, subPath),
  };
};

export const typeCheckerCheckObject = (
  checker: WithAnyTypeCheckerSchemaOptions<TypeCheckerObjectSchema>,
  value: unknown,
  path: string,
  check: TypeCheckerRecurseChecker
): TypeCheckerResult => {
  if (!isPlainObject(value)) {
    return typeCheckerResolveResult(
      checker,
      value,
      path,
      typeCheckerSchemaFailResult(value, path)
    );
  }
  let subResult: TypeCheckerObjectSubResult = {
    skip: false,
    key: "",
    result: typeCheckerSchemaFailResult(value, path),
  };
  let errors: TypeCheckerError[] = [];
  for (const orSchema of toArray(checker.schema)) {
    const validated: any = {};
    const mapsFromKeys: Record<string, true> = {};
    subResult = {
      skip: false,
      key: "",
      result: typeCheckerSchemaFailResult(value, path),
    };
    errors = [];
    let ok = true;
    for (const key in orSchema) {
      // @ts-expect-error implicit any
      const subSchemas = toArray(orSchema[key]);
      for (const subSchema of subSchemas) {
        subResult = checkObjectSubSchema(subSchema, key, value, path, check);
        if (
          subResult.skip ||
          subResult.result.status === TypeCheckerStatus.ok
        ) {
          break;
        }
      }
      if (subResult.skip) {
        continue;
      }
      const { result, key: effectiveKey } = subResult;
      mapsFromKeys[effectiveKey] = true;
      if (result.status === TypeCheckerStatus.ok) {
        validated[key] = result.value;
        errors.push(...result.errors);
      } else {
        ok = false;
        break;
      }
    }
    if (!ok) {
      continue;
    }
    const hasUnexpectedKey = objectHasUnexpectedKeys(
      orSchema,
      mapsFromKeys,
      value,
      path
    );
    if (hasUnexpectedKey) {
      errors.push(hasUnexpectedKey);
    }
    return typeCheckerOkResult(validated, errors);
  }
  return typeCheckerResolveResult(checker, value, path, subResult.result);
};
